import React, { useState, useEffect } from "react";
import { Col, Container, Row, Card } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import "react-bootstrap-typeahead/css/Typeahead.css";
import WhatsAppAPI from "../../api/WhatsAppAPI";
import Select from "react-select";
import jwt_decode from "jwt-decode";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CountryCode from "../../constants/CountryCode.json";

// Helper function to parse whatsapp_settings
const parseWhatsAppSettings = (settings) => {
  if (!settings) return [];
  if (Array.isArray(settings)) return settings;
  if (typeof settings === "string") {
    try {
      const parsed = JSON.parse(settings);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }
  return [];
};

// Helper function to parse allowed_modules
const parseAllowedModules = (modules) => {
  if (!modules) return [];
  if (Array.isArray(modules)) return modules;
  if (typeof modules === "string") {
    try {
      const parsed = JSON.parse(modules);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }
  return [];
};

const UserAdd = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(() => {
    // Parse the initial state
    const initialState = location.state ? { ...location.state } : {};
    if (initialState.whatsapp_settings) {
      initialState.whatsapp_settings = parseWhatsAppSettings(
        initialState.whatsapp_settings,
      );
    }
    if (initialState.allowed_modules) {
      initialState.allowed_modules = parseAllowedModules(
        initialState.allowed_modules,
      );
    }
    return initialState;
  });

  const mandatoryModules = ["whatsapp_template", "whatsapp_setting"];
  const [optionUsers, setOptionUsers] = useState([]);
  const [option, setoption] = useState();
  const [passwordError, setPasswordError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [whatsappError, setWhatsappError] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginUserRole, setLoginUserRole] = useState("");
  const [whatsappSetting, setWhatsappSetting] = useState([]);
  const [availableTabs, setAvailableTabs] = useState([]);
  const [selectedTabs, setSelectedTabs] = useState([]);
  const [hasUserPermissionModule, setHasUserPermissionModule] = useState(false);
  const USER_ROLES = [
    { value: "MANAGER", label: "Manager" },
    { value: "USER", label: "User" },
  ];

  useEffect(() => {
    fetchWhatsAppSetting();
    fetchAvailableTabs();

    if (user.id) {
      let temp = {};
      temp.value = user.managerid;
      temp.label = user.managername;
      setoption(temp);

      // Initialize selected tabs from user data (using allowed_modules)
      if (user.allowed_modules) {
        setSelectedTabs(parseAllowedModules(user.allowed_modules));
      }
    } else {
      let userInfo = jwt_decode(sessionStorage.getItem("token"));
      let temp = {};
      temp.value = userInfo.id;
      temp.label = userInfo.username;
      setoption(temp);
      setUser((prevUser) => ({
        ...prevUser,
        managerid: userInfo.id,
        userrole: "USER",
        isactive: true,
        managername: userInfo.username,
        is_whatsapp_number_hidden:
          prevUser.is_whatsapp_number_hidden !== undefined
            ? prevUser.is_whatsapp_number_hidden
            : false,
        can_export_leads: prevUser.can_export_leads || false,
        allowed_modules: parseAllowedModules(prevUser.allowed_modules) || [],
        whatsapp_settings:
          parseWhatsAppSettings(prevUser.whatsapp_settings) || [],
      }));
      setLoginUserRole(userInfo.userrole);
    }

    async function init() {
      const result = await WhatsAppAPI.fetchUsers();

      if (result) {
        let ar = [];
        var obj = {};
        obj.value = null;
        obj.label = "--Select--";
        ar.push(obj);
        result.map((item) => {
          if (item.userrole !== "USER") {
            var obj = {};
            obj.value = item.id;
            obj.label = item.username;
            ar.push(obj);
          }
        });
        setOptionUsers(ar);
      } else {
        setOptionUsers([]);
      }
    }
    init();
  }, [user.id]);

  const fetchWhatsAppSetting = async () => {
    try {
      const response = await WhatsAppAPI.getWhatsAppSettingRecord();
      if (response.success) {
        setWhatsappSetting(response.record);
      } else {
        setWhatsappSetting([]);
      }
    } catch (error) {
      console.error("Error fetching WhatsApp settings:", error);
      setWhatsappSetting([]);
    }
  };

  const fetchAvailableTabs = async () => {
    try {
      // Get tabs from JWT token or API
      const userInfo = jwt_decode(sessionStorage.getItem("token"));
      if (userInfo.modules) {
        const hasPermission = userInfo.modules.some(
          (m) => m.url === "user_permissions",
        );

        setHasUserPermissionModule(hasPermission);

        // Filter only top-level modules or all modules based on your requirement
        const modules = userInfo.modules
          .filter((module) => !module.parent_module) // Only top-level modules
          .map((module) => ({
            value: module.id,
            label: module.name,
            url: module.url,
          }));
        setAvailableTabs(modules);

        // Set default selected tabs for new users (check all by default)
        if (!user.id) {
          setSelectedTabs(modules);
        }
      }
    } catch (error) {
      console.error("Error fetching tabs:", error);
    }
  };

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isFormValid = () => {
    const isWhatsAppSettingsValid =
      user.userrole === "USER"
        ? Array.isArray(user.whatsapp_settings) &&
          user.whatsapp_settings.length > 0
        : true;

    return (
      Boolean(user.firstname?.trim()) &&
      Boolean(user.lastname?.trim()) &&
      Boolean(user.email?.trim()) &&
      Boolean(user.whatsapp_number?.trim()) &&
      Boolean(user.userrole?.trim()) &&
      Boolean(option?.value) &&
      !emailError &&
      passwordError === "" &&
      isWhatsAppSettingsValid
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const finalUser = {
      ...user,
      isactive: user.isactive !== undefined ? user.isactive : true,
      is_whatsapp_number_hidden:
        user.is_whatsapp_number_hidden !== undefined
          ? user.is_whatsapp_number_hidden
          : false,
      whatsapp_settings: user.whatsapp_settings || [],
      allowed_modules: selectedTabs, // Using allowed_modules to match DB column
      can_export_leads: user.can_export_leads || false, // Using can_export_leads to match DB column
    };

    try {
      if (!finalUser.managerid && option) {
        finalUser.managerid = option.value;
        finalUser.managername = option.label;
      }

      setIsSending(true);

      finalUser.country_code = finalUser.country_code
        ? String(finalUser.country_code).trim()
        : "+91";

      let result = {};
      if (finalUser.id) {
        result = await WhatsAppAPI.saveUser(finalUser);
      } else {
        result = await WhatsAppAPI.createUser(finalUser);
      }

      if (result.success) {
        toast.success("Record saved successfully.");
        navigate(`/users`);
      } else {
        if (typeof result.errors === "string") {
          toast.error(`${result.errors}`);
        } else if (Array.isArray(result.errors)) {
          const errorMessage = result.errors
            .map((error) => error.msg)
            .join(", ");
          toast.error(errorMessage);
        } else {
          toast.error("An unexpected error occurred while saving the record.");
        }
      }
    } catch (error) {
      toast.error("An error occurred while saving the record.");
    } finally {
      setIsSending(false);
    }
  };

  const handleCancel = () => {
    navigate(`/users/`);
  };

  const handleWhatsappNumberHidden = (e) => {
    setUser({ ...user, is_whatsapp_number_hidden: e.target.checked });
  };

  const handleExportLeadsChange = (e) => {
    setUser({ ...user, can_export_leads: e.target.checked });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setUser({ ...user, [name]: value });

    if (name === "password") {
      if (value.length < 8) {
        setPasswordError("Password must be at least 8 characters.");
      } else if (value.length > 16) {
        setPasswordError("Password cannot exceed 16 characters.");
      } else {
        setPasswordError("");
      }
    }

    if (name === "email") {
      setEmailError(!emailRegex.test(value) ? "Invalid email format." : "");
    }
  };

  const handleActive = (e) => {
    setUser({ ...user, isactive: e.target.checked });
  };

  const handleUsers = (event) => {
    setoption(event);
    setUser({ ...user, managerid: event.value, managername: event.label });
  };

  const handleTextOnlyChange = (e) => {
    const { name } = e.target;
    const value = e.target.value.replace(/[^A-Za-z ]/g, "");

    setUser({ ...user, [name]: value });
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const handleSelectAllTabs = () => {
    if (selectedTabs.length === availableTabs.length) {
      setSelectedTabs([]);
    } else {
      setSelectedTabs(availableTabs);
    }
  };

  // Safely get the value for the Select component
  const getWhatsAppSettingsValue = () => {
    if (!user.whatsapp_settings || !Array.isArray(user.whatsapp_settings)) {
      return [];
    }
    return user.whatsapp_settings.map((phone) => ({
      value: phone,
      label:
        whatsappSetting.find((setting) => setting.phone === phone)?.name ||
        phone,
    }));
  };

  return (
    <>
      <Container className="mt-5">
        <Row className="mx-5 text-center">
          <Col lg={12} xs={12} sm={12}>
            <div
              className=" text-center p-2"
              style={{
                height: "40px",
                backgroundColor: "#ffffff",
                borderRadius: "5px",
              }}
            >
              <span
                className="fw-semibold"
                style={{ color: "#605C68", fontSize: "large" }}
              >
                {user.id ? "Edit User" : "Add User"}
              </span>
            </div>
          </Col>
        </Row>
      </Container>

      <Container className="mt-1 mb-5">
        <Row className="mx-5">
          <Col lg={12} sm={12} xs={12} className="mb-2">
            <Card className="h-100" style={{ border: "none" }}>
              <Card.Body>
                <Row className="mb-3">
                  <Col lg={6} sm={12} xs={12}>
                    <Form.Group className="ms-3">
                      <Form.Label htmlFor="firstname">First Name</Form.Label>
                      <Form.Control
                        required
                        type="text"
                        name="firstname"
                        placeholder="Enter first name"
                        value={user.firstname || ""}
                        onChange={handleTextOnlyChange}
                        style={{ height: "36px" }}
                      />
                    </Form.Group>
                  </Col>
                  <Col lg={6} sm={12} xs={12}>
                    <Form.Group className="ms-3">
                      <Form.Label htmlFor="lastname">Last Name</Form.Label>
                      <Form.Control
                        required
                        type="text"
                        name="lastname"
                        placeholder="Enter lastname"
                        value={user.lastname || ""}
                        onChange={handleTextOnlyChange}
                        style={{ height: "36px" }}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col lg={2} sm={12} xs={12}>
                    <Form.Group className="ms-3">
                      <Form.Label htmlFor="country_code">
                        Country Code
                      </Form.Label>
                      <Form.Select
                        name="country_code"
                        value={user.country_code || "+91"}
                        onChange={(e) => handleChange(e)}
                        style={{ height: "36px" }}
                      >
                        {CountryCode.map((country, index) => (
                          <option key={index} value={country.country_code}>
                            {country.country} ({country.country_code})
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col lg={4} sm={12} xs={12}>
                    <Form.Group className="">
                      <Form.Label htmlFor="phone">Whatsapp Number</Form.Label>
                      <Form.Control
                        required
                        style={{ height: "36px" }}
                        type="text"
                        name="whatsapp_number"
                        placeholder="Enter Whatsapp Number"
                        value={user.whatsapp_number || ""}
                        onChange={handleChange}
                      />
                      {whatsappError ? (
                        <small className="text-danger"> {whatsappError}</small>
                      ) : (
                        ""
                      )}
                    </Form.Group>
                  </Col>
                  <Col lg={6} sm={12} xs={12}>
                    <Form.Group className="ms-3">
                      <Form.Label htmlFor="email">Email</Form.Label>
                      <Form.Control
                        style={{ height: "36px" }}
                        type="email"
                        required
                        name="email"
                        placeholder="Enter email"
                        value={user.email || ""}
                        onChange={handleChange}
                      />
                      {emailError && (
                        <small className="text-danger">{emailError}</small>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                {!user.id ? (
                  <Row className="mb-3">
                    <Col lg={6} sm={12} xs={12}>
                      <Form.Group className="ms-3">
                        <Form.Label htmlFor="password">Password</Form.Label>
                        <div className="d-flex align-items-center position-relative">
                          <Form.Control
                            type={showPassword ? "text" : "password"}
                            name="password"
                            placeholder="Enter Password"
                            value={user.password || ""}
                            onChange={handleChange}
                            required
                            style={{ height: "36px" }}
                          />
                          <span
                            className="position-absolute end-0 me-3"
                            onClick={togglePasswordVisibility}
                          >
                            <i
                              className={
                                !showPassword ? "fa fa-eye-slash" : "fa fa-eye"
                              }
                              aria-hidden="true"
                              style={{ cursor: "pointer" }}
                            ></i>
                          </span>
                        </div>

                        {passwordError && (
                          <small className="text-danger">
                            {" "}
                            {passwordError}
                          </small>
                        )}
                      </Form.Group>
                    </Col>
                    {user.userrole !== "ADMIN" && (
                      <Col lg={6} sm={12} xs={12}>
                        <Form.Group className="ms-3 mb-2">
                          <Form.Label htmlFor="userrole">
                            Assign WhatsApp Setting
                          </Form.Label>
                          <Select
                            isMulti
                            className="custom-select username"
                            options={whatsappSetting.map((setting) => ({
                              value: setting.phone,
                              label: `${setting.name} (${setting.phone})`,
                            }))}
                            value={getWhatsAppSettingsValue()}
                            onChange={(selected) => {
                              const selectedPhones = selected.map(
                                (option) => option.value,
                              );
                              setUser({
                                ...user,
                                whatsapp_settings: selectedPhones,
                              });
                            }}
                          />
                        </Form.Group>
                      </Col>
                    )}
                  </Row>
                ) : (
                  <Row className="mb-3">
                    {user.userrole !== "ADMIN" && (
                      <Col lg={6} sm={12} xs={12}>
                        <Form.Group className="ms-3 mb-2">
                          <Form.Label htmlFor="userrole">
                            Assign WhatsApp Setting
                          </Form.Label>
                          <Select
                            isMulti
                            className="custom-select username"
                            options={whatsappSetting.map((setting) => ({
                              value: setting.phone,
                              label: `${setting.name} (${setting.phone})`,
                            }))}
                            value={getWhatsAppSettingsValue()}
                            onChange={(selected) => {
                              const selectedPhones = selected.map(
                                (option) => option.value,
                              );
                              setUser({
                                ...user,
                                whatsapp_settings: selectedPhones,
                              });
                            }}
                          />
                        </Form.Group>
                      </Col>
                    )}
                  </Row>
                )}

                <Row className="mb-3">
                  <Col lg={6} sm={12} xs={12}>
                    <Form.Group className="ms-3">
                      <Form.Label>User Role</Form.Label>
                      <Form.Select
                        name="userrole"
                        value={user.userrole || "USER"}
                        onChange={handleChange}
                        style={{ height: "36px" }}
                      >
                        {USER_ROLES.map((role) => (
                          <option key={role.value} value={role.value}>
                            {role.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col lg={6} sm={12} xs={12}>
                    <Form.Group className="ms-3">
                      <Form.Label htmlFor="isactive">Active</Form.Label>
                      <Form.Check
                        name="isactive"
                        type="checkbox"
                        value="true"
                        checked={user.isactive === true}
                        id="inline-checkbox-9"
                        onChange={handleActive}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {/* Manager Selection */}
                <Row className="mb-3">
                  <Col lg={12} sm={12} xs={12}>
                    <Form.Group className="ms-3">
                      <Form.Label>
                        Select Manager <span className="text-danger">*</span>
                      </Form.Label>
                      <Select
                        className="custom-select username"
                        options={optionUsers}
                        value={option}
                        onChange={handleUsers}
                        placeholder="Select manager..."
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {/* Permissions Section - Only for USER role */}
                {user.userrole === "USER" && hasUserPermissionModule && (
                  <>
                    {/* Tab Visibility - Using allowed_modules to match DB */}
                    <Row className="mb-4">
                      <Col lg={12}>
                        <Card className="bg-light border-0">
                          <Card.Body>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <Form.Label className="fw-bold mb-0">
                                <i className="fa-solid fa-table-columns me-2"></i>
                                Allowed Modules (Tabs)
                              </Form.Label>
                              <Button
                                variant="link"
                                size="sm"
                                onClick={handleSelectAllTabs}
                                className="text-decoration-none"
                              >
                                {selectedTabs.length === availableTabs.length
                                  ? "Deselect All"
                                  : "Select All"}
                              </Button>
                            </div>

                            <Form.Text className="text-muted d-block mb-3">
                              Select which modules this user can access. All
                              modules are selected by default.
                            </Form.Text>

                            <Row>
                              {availableTabs.map((tab) => (
                                <Col
                                  lg={4}
                                  md={6}
                                  key={tab.value}
                                  className="mb-2"
                                >
                                  <Form.Check
                                    type="checkbox"
                                    id={`tab-${tab.value}`}
                                    label={tab.label}
                                    checked={
                                      mandatoryModules.includes(tab.url) ||
                                      selectedTabs.some(
                                        (t) => t.value === tab.value,
                                      )
                                    }
                                    disabled={mandatoryModules.includes(
                                      tab.url,
                                    )}
                                    onChange={(e) => {
                                      if (mandatoryModules.includes(tab.url))
                                        return;

                                      if (e.target.checked) {
                                        setSelectedTabs([...selectedTabs, tab]);
                                      } else {
                                        setSelectedTabs(
                                          selectedTabs.filter(
                                            (t) => t.value !== tab.value,
                                          ),
                                        );
                                      }
                                    }}
                                  />
                                </Col>
                              ))}
                            </Row>

                            {selectedTabs.length === 0 && (
                              <div className="text-warning small mt-2">
                                <i className="fa-solid fa-triangle-exclamation me-1"></i>
                                Warning: User won't have access to any modules
                                if none are selected
                              </div>
                            )}

                            <div className="mt-2">
                              <small className="text-muted">
                                <strong>Selected:</strong> {selectedTabs.length}{" "}
                                of {availableTabs.length} modules
                              </small>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>

                    {/* Export Permission - Using can_export_leads to match DB */}
                    <Row className="mb-4">
                      <Col lg={12}>
                        <Card className="bg-light border-0">
                          <Card.Body>
                            <Form.Group>
                              <div className="d-flex align-items-center">
                                <Form.Check
                                  type="switch"
                                  id="export-switch"
                                  checked={user.can_export_leads === true}
                                  onChange={handleExportLeadsChange}
                                  className="me-3"
                                />
                                <div>
                                  <Form.Label
                                    htmlFor="export-switch"
                                    className="fw-bold mb-1"
                                  >
                                    <i className="fa-solid fa-file-export me-2"></i>
                                    Can Export Leads
                                  </Form.Label>
                                  <Form.Text className="text-muted d-block">
                                    Allow user to export leads to CSV/Excel
                                  </Form.Text>
                                </div>
                              </div>
                            </Form.Group>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  </>
                )}

                {/* Privacy Settings */}
                <Row className="mb-4">
                  <Col lg={12}>
                    <Card className="bg-light border-0">
                      <Card.Body>
                        <Form.Group>
                          <div className="d-flex align-items-center">
                            <Form.Check
                              type="switch"
                              id="privacy-switch"
                              checked={user.is_whatsapp_number_hidden === true}
                              onChange={handleWhatsappNumberHidden}
                              className="me-3"
                            />
                            <div>
                              <Form.Label
                                htmlFor="privacy-switch"
                                className="fw-bold mb-1"
                              >
                                <i className="fa-solid fa-eye-slash me-2"></i>
                                Privacy Mode
                              </Form.Label>
                              <Form.Text className="text-muted d-block">
                                Hide lead WhatsApp numbers from all non-admin
                                users
                              </Form.Text>
                            </div>
                          </div>
                        </Form.Group>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                {/* Summary Section - Only for USER role */}
                {user.userrole === "USER" && hasUserPermissionModule && (
                  <Row className="mb-3">
                    <Col lg={12}>
                      <div className="bg-light p-3 rounded">
                        <h6 className="fw-bold mb-2">
                          User Permissions Summary
                        </h6>
                        <small className="text-muted d-block">
                          <i className="fa-solid fa-table me-2"></i>
                          Access to {selectedTabs.length} module(s)
                        </small>
                        <small className="text-muted d-block">
                          <i className="fa-solid fa-download me-2"></i>
                          {user.can_export_leads ? "Can" : "Cannot"} export
                          leads
                        </small>
                      </div>
                    </Col>
                  </Row>
                )}

                <Row>
                  <Col lg={12} sm={12} xs={12}>
                    <hr />
                  </Col>
                </Row>

                <Row className="g-0 mb-2">
                  <Col lg={12} sm={12} xs={12} className="text-end mt-1">
                    <Button
                      className="mx-2"
                      variant="light"
                      disabled={isSending}
                      onClick={handleCancel}
                    >
                      Back
                    </Button>
                    <Button
                      variant="outline-secondary"
                      disabled={!isFormValid()}
                      onClick={handleSubmit}
                    >
                      {isSending ? "Saving..." : "Save"}
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <ToastContainer />
      </Container>
    </>
  );
};

export default UserAdd;
